import { openingSpanOnWall } from './floorPlanWallGeometry.js';
import {
  findNearestWall,
  projectPointOntoSegment,
} from './floorPlanDesignerGeometry.js';
import {
  DEFAULT_DOOR,
  DEFAULT_DOOR_THICKNESS_M,
  DEFAULT_WINDOW,
  getWallSegmentsResolved,
} from './floorPlanDesignerState.js';
import { DESIGNER_FIXED_SCREEN_HIT_PX } from './floorPlanDesignerViewport.js';
import { resolveSelectableVisualState } from './floorPlanSelectableVisualState.js';

export const OPENING_2D_THICKNESS_M = DEFAULT_DOOR_THICKNESS_M;

export const OPENING_COLORS = {
  door: '#ea580c',
  window: '#06b6d4',
};

export const OPENING_OPACITY = {
  door: { normal: 0.35, hovered: 0.82, selected: 0.95 },
  window: { normal: 0.82, hovered: 0.82, selected: 0.95 },
};

/**
 * @param {{ type: 'door' | 'window'; isSelected?: boolean; isHovered?: boolean }} params
 */
export function resolveOpeningOpacity({ type, isSelected = false, isHovered = false }) {
  const visualState = resolveSelectableVisualState({ isSelected, isHovered });
  return OPENING_OPACITY[type][visualState];
}

/**
 * @param {number} value
 * @returns {number}
 */
export function clampCenterRatio(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0.5;
  return Math.max(0, Math.min(1, parsed));
}

/**
 * @param {number} wallLengthM
 * @param {number} widthM
 * @param {number} centerRatio
 * @returns {number}
 */
export function clampOpeningCenterRatio(wallLengthM, widthM, centerRatio) {
  const t = clampCenterRatio(centerRatio);
  if (wallLengthM <= 0) return t;
  const halfRatio = Math.min(0.5, widthM / 2 / wallLengthM);
  return Math.max(halfRatio, Math.min(1 - halfRatio, t));
}

/**
 * @param {number} displayScale
 * @returns {number}
 */
export function openingHitPaddingPx(displayScale) {
  const scale = Number.isFinite(displayScale) && displayScale > 0 ? displayScale : 1;
  return DESIGNER_FIXED_SCREEN_HIT_PX / scale;
}

/**
 * @param {[number, number]} p0
 * @param {[number, number]} p1
 * @returns {[number, number]}
 */
function segmentMidpoint(p0, p1) {
  return [(p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2];
}

/**
 * @param {import('./floorPlanDesignerState.js').DesignerState} state
 * @param {Array<{ id: string; start: [number, number]; end: [number, number] }>} segments
 * @param {number} mpp
 */
export function buildWallMountedOpeningItems(state, segments, mpp) {
  const segmentById = new Map(segments.map((seg) => [seg.id, seg]));
  return (state.openings || [])
    .filter((opening) => opening.type === 'door' || opening.type === 'window')
    .map((opening) => {
      const seg = segmentById.get(opening.wallId);
      if (!seg) return null;
      const widthM =
        Number(opening.widthMeters) ||
        (opening.type === 'door' ? DEFAULT_DOOR.widthMeters : DEFAULT_WINDOW.widthMeters);
      const thicknessM =
        Number(opening.thicknessMeters) || OPENING_2D_THICKNESS_M;
      const halfPx = (widthM / mpp) / 2;
      const centerRatio = clampCenterRatio(opening.centerRatio);
      const [p0, p1] = openingSpanOnWall(seg.start, seg.end, centerRatio, halfPx);
      const mid = segmentMidpoint(p0, p1);
      return {
        id: opening.id,
        opening,
        type: opening.type,
        wallId: opening.wallId,
        centerRatio,
        widthM,
        thicknessM,
        seg,
        p0,
        p1,
        mid,
        angle: Math.atan2(p1[1] - p0[1], p1[0] - p0[0]),
      };
    })
    .filter(Boolean);
}

/**
 * @param {{ start: [number, number]; end: [number, number] }} seg
 * @param {number} centerRatio
 * @param {number} halfPx
 * @param {number} thicknessPx
 */
export function openingPreviewLayout(seg, centerRatio, halfPx, thicknessPx) {
  const [p0, p1] = openingSpanOnWall(seg.start, seg.end, centerRatio, halfPx);
  const mid = segmentMidpoint(p0, p1);
  const widthPx = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
  const dx = seg.end[0] - seg.start[0];
  const dy = seg.end[1] - seg.start[1];
  return {
    x: mid[0],
    y: mid[1],
    widthPx: Math.max(widthPx, 1),
    thicknessPx,
    rotationDeg: (Math.atan2(dy, dx) * 180) / Math.PI,
  };
}

/**
 * @param {import('./floorPlanDesignerState.js').DesignerState} state
 * @param {[number, number]} pointer
 * @param {{ wallId: string; widthMeters?: number; type?: 'door' | 'window' }} opening
 * @param {{
 *   snapThresholdPx?: number;
 *   preferCurrentWallThresholdPx?: number;
 *   metersPerPixel: number;
 * }} options
 */
export function resolveOpeningWallPlacement(state, pointer, opening, options) {
  const {
    snapThresholdPx = 20,
    preferCurrentWallThresholdPx = 12,
    metersPerPixel,
  } = options;
  const widthM =
    Number(opening.widthMeters) ||
    (opening.type === 'window' ? DEFAULT_WINDOW.widthMeters : DEFAULT_DOOR.widthMeters);
  const segments = getWallSegmentsResolved(state);
  const currentSeg = segments.find((seg) => seg.id === opening.wallId);

  if (currentSeg) {
    const { dist, t } = projectPointOntoSegment(currentSeg.start, currentSeg.end, pointer);
    const wallLengthPx = Math.hypot(
      currentSeg.end[0] - currentSeg.start[0],
      currentSeg.end[1] - currentSeg.start[1],
    );
    const wallLengthM = wallLengthPx * metersPerPixel;
    const halfRatio = wallLengthM > 0 ? widthM / 2 / wallLengthM : 0.5;
    if (halfRatio < 0.5 && dist <= preferCurrentWallThresholdPx) {
      return {
        wallId: opening.wallId,
        centerRatio: clampOpeningCenterRatio(wallLengthM, widthM, t),
        seg: currentSeg,
      };
    }
  }

  const nearest = findNearestWall(state, pointer, snapThresholdPx);
  if (!nearest) return null;

  const seg = segments.find((item) => item.id === nearest.wallId);
  if (!seg) return null;
  const wallLengthPx = Math.hypot(seg.end[0] - seg.start[0], seg.end[1] - seg.start[1]);
  const wallLengthM = wallLengthPx * metersPerPixel;
  const halfRatio = wallLengthM > 0 ? widthM / 2 / wallLengthM : 0.5;
  if (halfRatio >= 0.5) return null;

  return {
    wallId: nearest.wallId,
    centerRatio: clampOpeningCenterRatio(wallLengthM, widthM, nearest.centerRatio),
    seg,
  };
}

/**
 * @param {{
 *   x: number;
 *   y: number;
 *   widthPx: number;
 *   thicknessPx: number;
 *   rotationDeg: number;
 * } | null | undefined} layout
 * @param {number} maxPreviewWidthPx
 */
export function isValidOpeningPreviewLayout(layout, maxPreviewWidthPx) {
  if (!layout) return false;
  const { x, y, widthPx, thicknessPx, rotationDeg } = layout;
  const maxWidth = Number.isFinite(maxPreviewWidthPx) && maxPreviewWidthPx > 0 ? maxPreviewWidthPx : Infinity;
  return (
    Number.isFinite(x) &&
    Number.isFinite(y) &&
    Number.isFinite(rotationDeg) &&
    Number.isFinite(widthPx) &&
    Number.isFinite(thicknessPx) &&
    widthPx > 0 &&
    widthPx < maxWidth &&
    thicknessPx > 0 &&
    thicknessPx < maxWidth
  );
}
